import React from 'react';
import classnames from 'classnames';
import { Url } from '@wix/da-url';
import includes from 'lodash/includes';
import { WithTranslation, withTranslation } from 'react-i18next';
import Button from '@wix/da-shared-react/pkg/Button';
import CloseButton from '@wix/da-shared-react/pkg/Primitives/CloseButton';
import {
  withMobileContext,
  WithMobileContextProps,
} from '@wix/da-react-context/pkg/MobileContext';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SisuPage, {
  SisuPageHeader,
  SisuPageSubHeader,
  SisuPageContent,
} from '../SisuPage';

import { CompliantLocalStoragePayload } from '@wix/da-shared-react/pkg/redux/compliantLocalStorage/actions';
import SisuForm from '../../SignupSignin/SisuForm';
import IntentCard from '../../SignupSignin/IntentCard';
import s from './IntentsPage.scss';

export interface Props {
  referer?: string;
  authMode: string;
  screenLoaded(authMode): void;
  toggleIntent(option, authMode, newState): void;
  toggleOther(authMode, newState): void;
  clickOtherDone(description, authMode): void;
  clickOtherCancel(authMode): void;
  clickSkip(authMode): void;
  clickContinue(authMode): void;
  setCompliantLocalStorageItem(CompliantLocalStoragePayload);
}
export interface State {
  selectedIntents: any;
  isOtherIntentActive: boolean;
  otherIntentDesc: string;
}

class IntentsPage extends React.Component<
  Props & WithTranslation & WithMobileContextProps,
  State
> {
  // backend ids are defined here: vms/lib/join/v./lib.join.php
  // they won't likely change - so just I duplicated them here
  private intents = [
    {
      id: 'discover',
      icon: Url.staticCDNLink('eclipse/sisu/intent-art.svg'),
      backendId: 1,
    },
    {
      id: 'improve',
      icon: Url.staticCDNLink('eclipse/sisu/intent-skills.svg'),
      backendId: 2,
    },
    {
      id: 'buy',
      icon: Url.staticCDNLink('eclipse/sisu/intent-buy.svg'),
      backendId: 5,
    },
    {
      id: 'promote',
      icon: Url.staticCDNLink('eclipse/sisu/intent-promote.svg'),
      backendId: 3,
    },
    {
      id: 'sell',
      icon: Url.staticCDNLink('eclipse/sisu/intent-sell.svg'),
      backendId: 4,
    },
    {
      id: 'other',
      icon: Url.staticCDNLink('eclipse/sisu/intent-other.svg'),
      backendId: 0,
    },
  ];
  private input: HTMLInputElement | null;
  state = {
    selectedIntents: [],
    isOtherIntentActive: false,
    otherIntentDesc: '',
  };

  componentDidMount() {
    this.props.screenLoaded(this.props.authMode);
  }

  componentDidUpdate() {
    if (this.state.isOtherIntentActive) {
      this.input && this.input.focus();
    }
  }

  render() {
    const { isMobile, t } = this.props;
    return (
      <SisuPage
        bodyClassName={s['body']}
        cardClassName={classnames(s['card'], s['topics-onboarding'])}
        logoClassName={s['outer-logo']}
        title={t('intents.page.title')}
        withHero={false}
        withLogo={!isMobile}
        isUnskippable
      >
        <SisuPageHeader className={s['header']}>
          {t('intents.title')}
        </SisuPageHeader>
        <SisuPageSubHeader className={s['sub-header']}>
          {t('intents.subheader')}
        </SisuPageSubHeader>
        <SisuForm
          className={s['intents-form']}
          action="/_sisu/do/saveintents"
          method="post"
        >
          <SisuPageContent className={s['content']}>
            <div id="intents" className={s['intents']}>
              {this.intents.map(this.renderIntentCard)}
              {this.renderOtherIntentCard()}
            </div>
          </SisuPageContent>
          {!this.state.isOtherIntentActive && this.renderFooter()}
        </SisuForm>
      </SisuPage>
    );
  }

  onClickContinue = () => {
    this.props.clickContinue(this.props.authMode);
  };

  renderIntentCard = intent => {
    const { t } = this.props;
    const { isOtherIntentActive, selectedIntents } = this.state;
    const isSelected =
      includes(selectedIntents, intent.id) ||
      (isOtherIntentActive && intent.id === 'other');

    return (
      <div
        key={intent.id}
        className={classnames(
          s['intent-wrapper'],
          isOtherIntentActive && intent.id !== 'other' && s['faded']
        )}
      >
        <IntentCard
          id={intent.id}
          iconSrc={intent.icon}
          title={t(`intents.list.${intent.id}.title`)}
          description={t(`intents.list.${intent.id}.description`)}
          onClick={this.onIntentClick}
          selected={isSelected}
          collapsed={isOtherIntentActive}
          disableHoverStyles={isOtherIntentActive && intent.id === 'other'}
        />
        {isSelected && (
          <input type="hidden" name="intents[]" value={intent.backendId} />
        )}
      </div>
    );
  };

  onIntentClick = clickedIntentId => {
    const selectedIntents: any = this.state.selectedIntents;
    const isClickedIntentSelected = includes(
      this.state.selectedIntents,
      clickedIntentId
    );
    let updatedIsOtherIntentActive = this.state.isOtherIntentActive;
    let updatedSelectedIntents = selectedIntents;
    if (clickedIntentId === 'other') {
      // clicking other doesn't select it
      // it is marked selected only after you enter the other intent text
      updatedSelectedIntents = selectedIntents;
      // but clicking toggles `isOtherIntentActive` flag that shows the text input box
      updatedIsOtherIntentActive = !this.state.isOtherIntentActive;

      this.props.toggleOther(this.props.authMode, updatedIsOtherIntentActive);
    } else {
      if (isClickedIntentSelected) {
        updatedSelectedIntents = selectedIntents.filter(
          selectedIntentId => selectedIntentId !== clickedIntentId
        );
      } else {
        updatedSelectedIntents = [...selectedIntents, clickedIntentId];
      }
      this.props.toggleIntent(
        clickedIntentId,
        this.props.authMode,
        !isClickedIntentSelected
      );
    }
    this.setState({
      selectedIntents: updatedSelectedIntents,
      isOtherIntentActive: updatedIsOtherIntentActive,
    });
  };

  renderOtherIntentCard() {
    const { t } = this.props;
    return (
      <div
        className={classnames(
          s['other-intent-wrapper'],
          this.state.isOtherIntentActive && s['active']
        )}
      >
        <CloseButton
          className={s['other-intent-close']}
          onClick={this.cancelOtherItent}
        />
        <div className={s['other-intent']}>
          <TextInput
            id="other-intent-desc"
            name="other_intent_desc"
            autoFocus
            label={t('intents.other.write_it_so_we_know')}
            ref={input => {
              if (input !== null) {
                this.input = input;
              }
            }}
            onChange={ev => this.onOtherIntentDescChange(ev.target.value)}
            value={this.state.otherIntentDesc}
            maxLength={100}
          />
          <div className={s['other-intent-footer']}>
            <button
              type="button"
              className={s['other-intent-cancel']}
              onClick={this.cancelOtherItent}
            >
              {t('intents.form.cancel')}
            </button>
            <button
              type="button"
              className={classnames(
                s['other-intent-done'],
                !this.state.otherIntentDesc && s['disabled']
              )}
              onClick={this.doneOtherItent}
            >
              {t('intents.form.done')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderFooter() {
    const { t } = this.props;
    return (
      <div className={s['footer']}>
        <Button
          className={s['submit-button']}
          onClick={this.onClickContinue}
          type="submit"
          disabled={!this.state.selectedIntents.length}
        >
          {t('intents.form.continue')}
        </Button>
      </div>
    );
  }

  onOtherIntentDescChange = value => {
    const selectedIntents: any = this.state.selectedIntents;
    let updatedSelectedIntents = selectedIntents;
    if (value) {
      const alreadyInList = includes(this.state.selectedIntents, 'other');
      if (!alreadyInList) {
        updatedSelectedIntents = [...selectedIntents, 'other'];
      }
    } else {
      updatedSelectedIntents = selectedIntents.filter(
        selectedIntentId => selectedIntentId !== 'other'
      );
    }
    this.setState({
      selectedIntents: updatedSelectedIntents,
      otherIntentDesc: value,
    });
  };

  cancelOtherItent = () => {
    const selectedIntents: any = this.state.selectedIntents;
    const updatedSelectedIntents = selectedIntents.filter(
      selectedIntentId => selectedIntentId !== 'other'
    );
    this.props.clickOtherCancel(this.props.authMode);
    this.setState({
      isOtherIntentActive: false,
      selectedIntents: updatedSelectedIntents,
      otherIntentDesc: '',
    });
  };

  doneOtherItent = () => {
    this.props.clickOtherDone(this.state.otherIntentDesc, this.props.authMode);
    this.setState({
      isOtherIntentActive: false,
    });
  };
}

export default withMobileContext(withTranslation()(IntentsPage));
